import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Checkbox, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { getCancelReasons } from "../../../../../service/order/orderService";

/**
 * "Some items are unavailable" inside the Accept dialog.
 *
 * Only for Tazty orders the Buyer API marked partial_cancel_allowed. The store
 * unticks what it cannot supply (or lowers the quantity), picks a reason, and
 * sees what the customer will pay. Lines are the customer's lines: a main item
 * and its add-ons move together.
 *
 * The amount mirrors the server rule (gross − Σ customer_value × units / qty);
 * the Seller API recomputes it, so this is only a preview.
 */

export interface PartialAcceptItem {
  id: number;
  name: string;
  qty: number;
  buyer_order_item_id?: number | null;
  customer_value?: number | null;
}

export interface PartialAcceptLine {
  buyerLineId: number;
  name: string;
  addons: string[];
  qty: number;
  customerValue: number;
}

export const groupPartialAcceptLines = (items: PartialAcceptItem[]): PartialAcceptLine[] => {
  const map = new Map<number, PartialAcceptLine>();
  const isMain = (it: PartialAcceptItem) => it.customer_value !== null && it.customer_value !== undefined;
  // Main rows first (they carry the customer's value), then their add-ons.
  for (const it of items) {
    const id = Number(it.buyer_order_item_id || 0);
    if (!id || !isMain(it)) continue;
    map.set(id, { buyerLineId: id, name: it.name, addons: [], qty: Number(it.qty), customerValue: Number(it.customer_value) });
  }
  for (const it of items) {
    const id = Number(it.buyer_order_item_id || 0);
    if (!id || isMain(it)) continue;
    map.get(id)?.addons.push(it.name);
  }
  return Array.from(map.values());
};

/** Units the store cannot supply, per buyer line. */
export type Unavailable = Record<number, number>;

export const partialPayable = (gross: number, lines: PartialAcceptLine[], unavailable: Unavailable) => {
  let cancelled = 0;
  for (const line of lines) {
    const units = unavailable[line.buyerLineId] || 0;
    if (units > 0 && line.qty > 0) {
      cancelled += Math.round((Math.round(line.customerValue * 100) * Math.min(units, line.qty)) / line.qty);
    }
  }
  return Math.max(0, Math.round(gross * 100) - cancelled) / 100;
};

export const hasUnavailable = (unavailable: Unavailable) =>
  Object.values(unavailable).some((n) => Number(n) > 0);

/**
 * Every line marked unavailable. There is no order left to accept, so the
 * store wants Cancel Order - saying "choose a reason" here would send them
 * looking for a dropdown that is deliberately hidden.
 */
export const allUnavailable = (
  items: PartialAcceptItem[],
  unavailable: Unavailable,
) => {
  const lines = groupPartialAcceptLines(items);
  return (
    lines.length > 0 &&
    lines.every((l) => (unavailable[l.buyerLineId] || 0) >= l.qty)
  );
};

interface Props {
  items: PartialAcceptItem[];
  grossAmount: number;
  unavailable: Unavailable;
  onChange: (next: Unavailable) => void;
  cancelReasonId: number | "";
  onCancelReasonChange: (id: number | "") => void;
}

const PartialAcceptPanel: React.FC<Props> = ({
  items,
  grossAmount,
  unavailable,
  onChange,
  cancelReasonId,
  onCancelReasonChange,
}) => {
  const lines = useMemo(() => groupPartialAcceptLines(items), [items]);
  const [reasons, setReasons] = useState<{ label: string; value: number }[]>([]);
  const anyUnavailable = hasUnavailable(unavailable);
  // The store tried to remove the last item still in the order.
  const [lastItemBlocked, setLastItemBlocked] = useState(false);

  useEffect(() => {
    if (!anyUnavailable || reasons.length > 0) return;
    getCancelReasons()
      .then((res: any) => {
        const rows = res?.data?.data ?? [];
        setReasons(rows.map((r: any) => ({ label: r.reason, value: r.id })));
      })
      .catch(() => undefined);
  }, [anyUnavailable, reasons.length]);

  if (lines.length === 0) return null;

  const allGone = lines.every((l) => (unavailable[l.buyerLineId] || 0) >= l.qty);
  const suppliedUnits = lines.reduce((sum, l) => sum + l.qty - (unavailable[l.buyerLineId] || 0), 0);
  const set = (id: number, units: number) => {
    const line = lines.find((l) => l.buyerLineId === id);
    const next = Math.min(Math.max(0, units), line ? line.qty : units);
    const current = unavailable[id] || 0;
    // At least one unit must stay: removing everything is Cancel Order.
    if (next > current && suppliedUnits - (next - current) <= 0) {
      setLastItemBlocked(true);
      return;
    }
    setLastItemBlocked(false);
    onChange({ ...unavailable, [id]: next });
  };
  const payable = partialPayable(grossAmount, lines, unavailable);

  return (
    <Box sx={{ px: 3, pb: 2 }}>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 0.5 }}>
        Items you can supply
      </Typography>
      <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", mb: 1 }}>
        Untick an item or lower its quantity if it is unavailable.
      </Typography>
      {lines.map((line) => {
        const missing = unavailable[line.buyerLineId] || 0;
        const supplied = line.qty - missing;
        return (
          <Stack key={line.buyerLineId} direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
            <Checkbox
              size="small"
              checked={supplied > 0}
              onChange={(e) => set(line.buyerLineId, e.target.checked ? 0 : line.qty)}
              data-testid={`partial-accept-item-${line.buyerLineId}`}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  textDecoration: supplied === 0 ? "line-through" : "none",
                  color: supplied === 0 ? "text.disabled" : "text.primary",
                }}
                noWrap
              >
                {line.name}
              </Typography>
              {line.addons.length > 0 && (
                <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }} noWrap>
                  + {line.addons.join(", ")}
                </Typography>
              )}
            </Box>
            {line.qty > 1 && (
              <Stack direction="row" alignItems="center">
                <IconButton size="small" disabled={supplied <= 0 || suppliedUnits <= 1} onClick={() => set(line.buyerLineId, missing + 1)}>
                  <RemoveIcon fontSize="inherit" />
                </IconButton>
                <Typography sx={{ fontSize: "0.8rem", minWidth: 36, textAlign: "center" }}>
                  {supplied}/{line.qty}
                </Typography>
                <IconButton size="small" disabled={missing <= 0} onClick={() => set(line.buyerLineId, missing - 1)}>
                  <AddIcon fontSize="inherit" />
                </IconButton>
              </Stack>
            )}
          </Stack>
        );
      })}

      {lastItemBlocked && !allGone && (
        <Alert severity="warning" sx={{ fontSize: "0.78rem", mt: 1 }}>
          At least one item must stay. If you don't have any items, please cancel the order.
        </Alert>
      )}

      {anyUnavailable && (
        <Box sx={{ mt: 1 }}>
          {allGone ? (
            <Alert severity="warning" sx={{ fontSize: "0.78rem" }}>
              At least one item must stay. If you don't have any items, please cancel the order.
            </Alert>
          ) : (
            <>
              <TextField
                select
                fullWidth
                size="small"
                label="Reason"
                value={cancelReasonId}
                onChange={(e) => onCancelReasonChange(e.target.value === "" ? "" : Number(e.target.value))}
                sx={{ mb: 1 }}
              >
                {reasons.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </TextField>
              <Alert severity="info" sx={{ fontSize: "0.78rem" }}>
                Customer pays <b>₹{payable.toFixed(2)}</b> (was ₹{Number(grossAmount).toFixed(2)})
              </Alert>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PartialAcceptPanel;
